import { Fragment } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * The bot's reply, with its links made tappable.
 *
 * WHY A COMPONENT AND NOT `whiteSpace: pre-wrap` ANY MORE. The support bot's
 * most common way of misleading somebody was inventing app navigation — it
 * once told a member to open «بخش وام‌ها یا پرداخت‌ها» and tap «پرداخت قسط با
 * کارت‌به‌کارت» to upload a receipt, and none of that exists. The backend fix
 * is to stop describing routes and hand over a real link instead (it now
 * refuses to emit any URL a tool did not produce). That fix only reaches the
 * member if the link is something they can press: a URL rendered as plain text
 * in a chat bubble is, on a phone, just a long word.
 *
 * Internal links navigate through the router rather than reloading. The PWA is
 * a single page behind a service worker and a JWT; a full page load to reach
 * our own /easy-pay would throw away the app and warm start it again for no
 * reason.
 */

// Matches what the backend allows itself to send: absolute http(s) URLs, plus
// the two bare messenger handles from the knowledge base («eitaa.com/Hamyanapp»,
// «t.me/hamyanapp»). Kept deliberately close to the server's own pattern — a
// client that linkified more than the server validates would be a way back in
// for exactly the invented destinations the server just stopped emitting.
const URL_PATTERN =
    /(?:https?:\/\/)?(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}(?:\/[^\s،؛«»"'()[\]]*)?/g

const PWA_HOSTS = ['pwa.hamyanapp.com', 'gethamyan.ir']

/** The in-app path for one of our own URLs, or null for anything external. */
const internalPath = (raw: string) => {
    try {
        const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`)
        if (!PWA_HOSTS.includes(url.hostname)) return null
        // /t/<code>/ and /avatars/… are Django, not router routes.
        if (url.hostname === 'gethamyan.ir') return null
        return `${url.pathname}${url.search}`
    } catch {
        return null
    }
}

const BotMessage = ({
    text,
    onNavigate,
}: {
    text: string
    /** Lets the parent close the modal — and remember the thread — first. */
    onNavigate: () => void
}) => {
    const navigate = useNavigate()

    // Trailing sentence punctuation belongs to the sentence, not the link;
    // «…/easy-pay.» must not navigate to a path with a full stop on the end.
    const trim = (candidate: string) => {
        let value = candidate
        let tail = ''
        while (value && '.،؛:!؟'.includes(value[value.length - 1])) {
            tail = value[value.length - 1] + tail
            value = value.slice(0, -1)
        }
        return { value, tail }
    }

    const pieces: JSX.Element[] = []
    let cursor = 0
    let key = 0

    text.replace(URL_PATTERN, (match, offset: number) => {
        const { value, tail } = trim(match)
        const before = text.slice(cursor, offset)
        cursor = offset + match.length
        if (before) {
            pieces.push(<Fragment key={key++}>{before}</Fragment>)
        }

        const path = internalPath(value)
        const className = 'text-green-1 underline break-all'
        pieces.push(
            path ? (
                <button
                    key={key++}
                    type="button"
                    className={className}
                    onClick={() => {
                        onNavigate()
                        navigate(path)
                    }}
                >
                    {value}
                </button>
            ) : (
                <a
                    key={key++}
                    href={value.startsWith('http') ? value : `https://${value}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={className}
                >
                    {value}
                </a>
            )
        )
        if (tail) pieces.push(<Fragment key={key++}>{tail}</Fragment>)
        return match
    })

    const rest = text.slice(cursor)
    if (rest) pieces.push(<Fragment key={key++}>{rest}</Fragment>)

    // The server strips Markdown, so newlines are the only formatting left.
    return <span style={{ whiteSpace: 'pre-wrap' }}>{pieces}</span>
}

export default BotMessage
